import { useCallback, useRef } from 'react';
import type {
  DomainRouterMutationArgs,
  DomainRouterMutationData,
  DomainRouterMutationName,
  DomainRouterQueryArgs,
  DomainRouterQueryData,
  DomainRouterQueryName,
} from '../api/domainRouterClient';
import {
  domainRouterUniversalApi,
  type DomainMutationRequest,
  type DomainQueryRequest,
} from './domainRouterUniversalApi';

const toQueryRequest = (
  operation: DomainRouterQueryName,
  args: unknown
): DomainQueryRequest => ({
  operation,
  args,
}) as unknown as DomainQueryRequest;

const toMutationRequest = (
  operation: DomainRouterMutationName,
  args: unknown
): DomainMutationRequest => ({
  operation,
  args,
}) as unknown as DomainMutationRequest;

type DomainQueryHookOptions = Record<string, unknown> | undefined;

type DomainQueryStatus = 'uninitialized' | 'pending' | 'fulfilled' | 'rejected';

type DomainQueryHookBaseResult = {
  data?: unknown;
  currentData?: unknown;
  error?: unknown;
  endpointName?: string;
  fulfilledTimeStamp?: number;
  startedTimeStamp?: number;
  originalArgs?: DomainQueryRequest;
  requestId?: string;
  status?: DomainQueryStatus;
  isUninitialized: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  isError: boolean;
  refetch: () => Promise<unknown>;
};

type LazyDomainQueryTriggerResult = {
  unwrap: () => Promise<unknown>;
};

type LazyDomainQueryTrigger = (
  request: DomainQueryRequest,
  preferCacheValue?: boolean,
) => LazyDomainQueryTriggerResult;

type LazyDomainQueryHookBaseResult = readonly [
  LazyDomainQueryTrigger,
  DomainQueryHookBaseResult,
  unknown,
];

type DomainMutationHookBaseResult = {
  data?: unknown;
  error?: unknown;
  endpointName?: string;
  fulfilledTimeStamp?: number;
  startedTimeStamp?: number;
  originalArgs?: DomainMutationRequest;
  requestId?: string;
  status?: DomainQueryStatus;
  isUninitialized: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  reset: () => void;
};

type DomainMutationTriggerResult = {
  unwrap: () => Promise<unknown>;
};

type DomainMutationTrigger = (request: DomainMutationRequest) => DomainMutationTriggerResult;

type DomainMutationHookBaseTuple = readonly [DomainMutationTrigger, DomainMutationHookBaseResult];

const useUntypedDomainQueryQuery = domainRouterUniversalApi.useDomainQueryQuery as (
  request: DomainQueryRequest,
  options?: DomainQueryHookOptions,
) => DomainQueryHookBaseResult;

const useUntypedLazyDomainQueryQuery =
  domainRouterUniversalApi.useLazyDomainQueryQuery as () => LazyDomainQueryHookBaseResult;
const useUntypedDomainMutationMutation =
  domainRouterUniversalApi.useDomainMutationMutation as () => DomainMutationHookBaseTuple;

export const useDomainRouterQuery = <Name extends DomainRouterQueryName>(
  operation: Name,
  args: DomainRouterQueryArgs<Name>,
  options?: DomainQueryHookOptions
) => {
  const result = useUntypedDomainQueryQuery(toQueryRequest(operation, args), options);
  return result as unknown as Omit<DomainQueryHookBaseResult, 'data' | 'currentData'> & {
    data: DomainRouterQueryData<Name> | undefined;
    currentData: DomainRouterQueryData<Name> | undefined;
  };
};

export const useLazyDomainRouterQuery = <Name extends DomainRouterQueryName>() => {
  const [trigger, result, lastPromiseInfo] = useUntypedLazyDomainQueryQuery();
  const triggerRef = useRef(trigger);
  triggerRef.current = trigger;

  const typedTrigger = useCallback(
    (operation: Name, args: DomainRouterQueryArgs<Name>, preferCacheValue?: boolean) =>
      triggerRef.current(toQueryRequest(operation, args), preferCacheValue).unwrap() as Promise<DomainRouterQueryData<Name>>,
    []
  );

  return [
    typedTrigger,
    result as unknown as Omit<DomainQueryHookBaseResult, 'data' | 'currentData'> & {
      data: DomainRouterQueryData<Name> | undefined;
      currentData: DomainRouterQueryData<Name> | undefined;
    },
    lastPromiseInfo,
  ] as const;
};

export const useDomainRouterMutation = <Name extends DomainRouterMutationName>(operation: Name) => {
  const [trigger, mutationState] = useUntypedDomainMutationMutation();

  const typedTrigger = useCallback(
    (args: DomainRouterMutationArgs<Name>) =>
      trigger(toMutationRequest(operation, args)).unwrap() as Promise<DomainRouterMutationData<Name>>,
    [operation, trigger]
  );

  return [
    typedTrigger,
    mutationState as unknown as Omit<DomainMutationHookBaseResult, 'data'> & {
      data: DomainRouterMutationData<Name> | undefined;
    },
  ] as const;
};
